import React, { useMemo } from "react";

const TZ_OPTIONS = [
  { label: "8pm ET", abbr: "ET" },
  { label: "7pm CT", abbr: "CT" },
  { label: "6pm MT", abbr: "MT" },
  { label: "5pm PT", abbr: "PT" },
];

const TIMEZONE_GROUPS = {
  ET: ["America/New_York", "America/Detroit", "America/Indiana/Indianapolis", "America/Kentucky/Louisville", "US/Eastern"],
  CT: ["America/Chicago", "America/Indiana/Knox", "America/Indiana/Tell_City", "America/Menominee", "America/North_Dakota/Beulah", "America/North_Dakota/Center", "America/North_Dakota/New_Salem", "US/Central"],
  MT: ["America/Denver", "America/Boise", "US/Mountain"],
  PT: ["America/Los_Angeles", "US/Pacific"],
};

const SHORT_TIMEZONE_GROUPS = {
  EST: "ET", EDT: "ET",
  CST: "CT", CDT: "CT",
  MST: "MT", MDT: "MT",
  PST: "PT", PDT: "PT",
};

const detectPrimaryUsTimezone = () => {
  try {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const shortTimezone = new Intl.DateTimeFormat("en-US", {
      timeZone: browserTimezone,
      timeZoneName: "short",
    }).formatToParts(new Date()).find((part) => part.type === "timeZoneName")?.value;
    return SHORT_TIMEZONE_GROUPS[shortTimezone]
      || Object.entries(TIMEZONE_GROUPS).find(([, zones]) => zones.includes(browserTimezone))?.[0]
      || null;
  } catch {
    return null;
  }
};

const pad2 = (n) => String(n).padStart(2, "0");

export const ClassCard = ({ schedule }) => {
  const activeTimezone = useMemo(() => detectPrimaryUsTimezone(), []);
  const totalSec = Math.max(0, Math.floor(schedule.remainingMs / 1000));
  const days = Math.floor(totalSec / 86400);
  const hrs  = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;

  const dateStr = new Intl.DateTimeFormat("en-US", {
    weekday: "long", month: "long", day: "numeric",
    timeZone: "America/New_York",
  }).format(schedule.classStart);

  return (
    <div
      className="w-full -translate-y-2 rounded-xl p-6 md:p-8"
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.18)",
      }}
      data-testid="class-card"
    >
      {schedule.isLive && (
        <div className="mb-3 flex justify-center" data-testid="class-live-badge-wrap">
          <span className="live-class-pulse inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-white" style={{ background: "#DC2626" }} data-testid="class-live-badge">
            <span className="h-2 w-2 rounded-full bg-white" aria-hidden="true" />
            Live
          </span>
        </div>
      )}

      <p
        className={`${schedule.isLive ? "" : "next-class-attention"} text-center font-display`}
        style={{
          color: "#ffffff",
          fontSize: "clamp(1.35rem, 2.5vw, 1.85rem)",
          lineHeight: 1.3,
        }}
        data-testid="class-card-next"
      >
        {schedule.isLive
          ? "Class in Session: EazyBot Class — Join Now"
          : <>Next Live Class &mdash; {dateStr}</>}
      </p>

      {/* Countdown — slightly smaller than the hero */}
      <div
        className="mt-5 flex items-center justify-center gap-3 md:gap-4"
        data-testid="class-card-countdown"
      >
        <span className="italic text-sm hidden sm:inline" style={{ color: "#D4A24C" }}>
          Starts in
        </span>
        {[
          { v: days, l: "DAYS" },
          { v: hrs,  l: "HRS"  },
          { v: mins, l: "MIN"  },
          { v: secs, l: "SEC"  },
        ].map((cell, i, arr) => (
          <React.Fragment key={cell.l}>
            <div className="flex flex-col items-center">
              <div
                className="font-display tabular-nums rounded-lg px-3 py-2 min-w-[52px] md:min-w-[60px] text-center"
                style={{
                  border: "1px solid rgba(212,162,76,0.45)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
                  fontSize: "clamp(1.15rem, 2vw, 1.55rem)",
                  background: "linear-gradient(135deg, #7B5500 0%, #C8A020 25%, #F5D050 50%, #C8A020 75%, #7B5500 100%)",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                  filter: "drop-shadow(0 0 6px rgba(212,162,76,0.5))",
                }}
              >
                {pad2(cell.v)}
              </div>
              <span className="mt-1 text-[10px] font-mono-label" style={{ color: "rgba(212,162,76,0.65)" }}>{cell.l}</span>
            </div>
            {i < arr.length - 1 && (
              <span className="text-lg md:text-xl" style={{ color: "rgba(212,162,76,0.5)" }}>:</span>
            )}
          </React.Fragment>
        ))}
      </div>

      <div className="mt-5 text-center" data-testid="class-card-local-time">
        <p className="font-mono-label text-xs font-semibold" style={{ color: "#7DD3FC", letterSpacing: "0.12em", textTransform: "none" }}>Your Local Time</p>
        <div className="mt-3 flex flex-wrap items-center justify-center gap-2" data-testid="class-card-timezone-pills">
          {TZ_OPTIONS.map((option) => {
            const active = option.abbr === activeTimezone;
            return (
              <span
                key={option.abbr}
                className="rounded-full border px-3.5 py-1.5 text-sm font-semibold"
                style={{
                  borderColor: active ? "#D4A24C" : "rgba(255,255,255,0.25)",
                  background: active ? "#B8852A" : "rgba(255,255,255,0.04)",
                  color: active ? "#ffffff" : "rgba(255,255,255,0.85)",
                  boxShadow: active ? "0 0 14px rgba(212,162,76,0.4)" : "none",
                }}
                data-testid={`class-time-${option.abbr.toLowerCase()}`}
                data-active={active ? "true" : "false"}
              >
                {option.label}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
};
